import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { exitReasonLabel, type Device, type ExitReason, type IssueSeverity, type TestBehaviour, type VisitExit, type VisitStatus } from '@daevio/shared';
import { formatDuration, pathOf } from '../lib/format.ts';
import { severityLabel, statusLabel } from '../lib/synthetic.ts';
import { Icon, Orb, deviceIcon, initialsOf } from './ui.tsx';

/*
 * The pieces of a test report, shared by the signed-in report and the public one: the verdict and the moment
 * it broke down, one lane per visitor (where they landed, how far they went, where and why they stopped), the problems
 * worst first, and the section bar that keeps them one click apart.
 */

/** A visitor as the report shows it; the app and the public report both map their visits to this. */
export interface LaneVisit {
  key: string;
  /** Position in the test: picks the visitor's orb. */
  index: number;
  personaName: string;
  device: Device;
  status: VisitStatus;
  exit: VisitExit | null;
  outcome: string | null;
  stepCount: number;
  durationMs: number | null;
  finalMood: number | null;
  /** Waiting at a sign-in page for an account. */
  waiting?: boolean;
  /** The page they landed on, when known. */
  entryPath?: string | null;
  /** Why they came, in a few words ("Google search"). */
  source?: string | null;
  /** The visit's page, and the same page at its last step. */
  href: string;
  endHref: string;
}

export type LaneEnd = 'live' | 'queued' | 'reached' | 'left' | 'signin' | 'other';

const OPEN = new Set<VisitStatus>(['queued', 'running']);

export function laneEnd(v: Pick<LaneVisit, 'status'>): LaneEnd {
  if (v.status === 'queued') return 'queued';
  if (v.status === 'running') return 'live';
  if (v.status === 'converted' || v.status === 'satisfied') return 'reached';
  if (v.status === 'abandoned') return 'left';
  if (v.status === 'login_wall') return 'signin';
  return 'other';
}

/** The page where the visit ended, without its query. */
export function endPathOf(v: Pick<LaneVisit, 'exit'>): string | null {
  return v.exit?.url ? pathOf(v.exit.url).split('?')[0]! : null;
}

function median(xs: number[]): number | null {
  if (xs.length === 0) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

/** Median visit, mood at the end and steps of the visits that ended. */
export function statsOf(visits: Pick<LaneVisit, 'status' | 'durationMs' | 'finalMood' | 'stepCount'>[]) {
  const ended = visits.filter((v) => !OPEN.has(v.status));
  return {
    ms: median(ended.map((v) => v.durationMs).filter((x): x is number => x !== null)),
    mood: median(ended.map((v) => v.finalMood).filter((x): x is number => x !== null)),
    steps: median(ended.map((v) => v.stepCount)),
  };
}

/** Sentences of a paragraph; paths and decimals ("/en/sign-in.", "1.5") do not end one. */
export function sentencesOf(text: string): string[] {
  return text
    .split(/(?<=[.!?])\s+(?=["“'(]?[\p{Lu}\d])/u)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * What the report opens with: a short verdict, one line on why, and the key points. Reports written before the
 * editing pass have one long summary: its first sentence is the verdict when it is short, the others become points.
 */
export function reportLead(report: { headline?: string; summary: string; highlights: string[] } | null | undefined, fallback: string): { headline: string; summary: string; points: string[] } {
  if (!report) return { headline: fallback, summary: '', points: [] };
  if (report.headline) return { headline: report.headline, summary: report.summary, points: report.highlights };
  const sentences = sentencesOf(report.summary);
  const first = sentences[0] ?? '';
  const short = first.length > 0 && first.length <= 110;
  const rest = short ? sentences.slice(1) : sentences;
  return { headline: short ? first : fallback, summary: '', points: rest.length > 0 ? rest : report.highlights };
}

/** The key points under the verdict: a few short lines instead of a paragraph. */
export function KeyPoints({ points, label = 'In short' }: { points: string[]; label?: string }) {
  if (points.length === 0) return null;
  return (
    <div className="r3-points" data-testid="key-points">
      <span className="r3-points-label">{label}</span>
      <ul>
        {points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </div>
  );
}

/** A long text folded to a few lines, with a button to read it all. */
export function FoldedText({ text, className, lines = 3 }: { text: string; className?: string; lines?: number }) {
  const [open, setOpen] = useState(false);
  const long = text.length > lines * 90;
  return (
    <div className={className}>
      <p className={long && !open ? 'r3-folded' : undefined} style={long && !open ? { WebkitLineClamp: lines } : undefined}>
        {text}
      </p>
      {long && (
        <button type="button" className="r3-unfold" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
          {open ? 'Less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

/** The report's downloads and link, at the end of the page: the PDF to keep, the page to share. */
export function ExportCard({ onPdf, downloading, share, error }: { onPdf?: () => void; downloading?: boolean; share?: ReactNode; error?: string | null }) {
  return (
    <section className="r3-section r3-export-section" aria-label="Download the report">
      <div className="r3-wrap">
        <div className="r3-export">
          <span className="r3-export-icon" aria-hidden="true">
            <Icon name="download" size={22} />
          </span>
          <div>
            <h2>Take the report with you</h2>
            <p>A PDF with the verdict, every problem with the fix and a link to its replay, and the prompt. Ready to send or print.</p>
          </div>
          <div className="r3-export-actions">
            {onPdf && (
              <button className="button primary" type="button" onClick={onPdf} disabled={downloading} data-testid="export-pdf">
                <Icon name="download" /> {downloading ? 'Preparing the PDF…' : 'Download PDF'}
              </button>
            )}
            {share}
          </div>
          {error && <p className="form-error r3-export-error">{error}</p>}
        </div>
      </div>
    </section>
  );
}

/** The night band's figures: median visit, mood at the end, steps, problems. */
export function VerdictStats({ visits, problems, reached }: { visits: LaneVisit[]; problems?: number; reached?: { n: number; of: number } }) {
  const s = statsOf(visits);
  return (
    <dl className="r3-stats">
      {reached && (
        <div>
          <dt>Reached their goal</dt>
          <dd>
            {reached.n}
            <small> of {reached.of}</small>
          </dd>
        </div>
      )}
      {s.ms !== null && (
        <div>
          <dt>Median visit</dt>
          <dd>{formatDuration(s.ms)}</dd>
        </div>
      )}
      {s.mood !== null && (
        <div>
          <dt>Mood at the end</dt>
          <dd>
            {Math.round(s.mood)}
            <small>/10</small>
          </dd>
        </div>
      )}
      {s.steps !== null && !reached && (
        <div>
          <dt>Steps per visit</dt>
          <dd>{Math.round(s.steps)}</dd>
        </div>
      )}
      {problems !== undefined && (
        <div>
          <dt>Problems found</dt>
          <dd>{problems}</dd>
        </div>
      )}
    </dl>
  );
}

/** The moment the report leads with: one visitor's words, where and when they said them. */
export function MomentQuote({
  personaName,
  index,
  gaveUp,
  thought,
  path,
  clock,
  href,
  visitHref,
}: {
  personaName: string;
  index: number;
  /** They left or the visit broke down; otherwise it is how a visit that went fine ended. */
  gaveUp: boolean;
  thought: string;
  path: string | null;
  clock: string | null;
  href: string;
  visitHref: string;
}) {
  return (
    <figure className="r3-moment">
      <figcaption>
        <Orb index={index} size={36} />
        <span className="r3-moment-who">
          <strong>{personaName}</strong>
          <span>
            {gaveUp ? 'The moment they gave up' : 'How their visit ended'}
            {path && (
              <>
                , on <code>{path}</code>
              </>
            )}
          </span>
        </span>
        {clock && <span className={gaveUp ? 'r3-clock' : 'r3-clock r3-clock-quiet'}>{clock}</span>}
      </figcaption>
      <blockquote className="voice">“{thought}”</blockquote>
      <div className="r3-moment-actions">
        <Link className="button primary" to={href}>
          <Icon name="play" size={14} /> Watch this moment
        </Link>
        <Link className="r3-quiet-link" to={visitHref}>
          See the whole visit
        </Link>
      </div>
    </figure>
  );
}

/** The bar under the night band: the report's sections, and its one action on the right. */
export function ReportNav({ items, action }: { items: { id: string; label: string; count?: number }[]; action?: ReactNode }) {
  return (
    <nav className="r3-nav" aria-label="Report sections">
      <div className="r3-wrap r3-nav-row">
        {items.map((i) => (
          <a key={i.id} href={`#${i.id}`} className="r3-nav-link">
            {i.label}
            {i.count !== undefined && <span className="r3-count">{i.count}</span>}
          </a>
        ))}
        {action && <span className="r3-nav-action">{action}</span>}
      </div>
    </nav>
  );
}

/** "All 5 left within 1m 06s." and the reasons, counted. */
function lanesHeadline(visits: LaneVisit[], behaviour: TestBehaviour, live: boolean): { title: string; reasons: [ExitReason, number][] } {
  const ended = visits.filter((v) => !OPEN.has(v.status));
  const n = ended.length;
  const reasons = new Map<ExitReason, number>();
  for (const v of ended) if (v.exit && laneEnd(v) !== 'reached') reasons.set(v.exit.category, (reasons.get(v.exit.category) ?? 0) + 1);
  const sorted = [...reasons.entries()].sort((a, b) => b[1] - a[1]);
  if (live) return { title: `${n} of ${visits.length} ${visits.length === 1 ? 'visit has' : 'visits have'} ended.`, reasons: sorted };
  if (behaviour === 'check') return { title: `${n} ${n === 1 ? 'inspector' : 'inspectors'} went through the site.`, reasons: [] };
  const left = ended.filter((v) => laneEnd(v) === 'left').length;
  const reached = ended.filter((v) => laneEnd(v) === 'reached').length;
  // Stopped at a sign-in page without an account: not a choice of theirs, counted apart from those who left.
  const signin = ended.filter((v) => laneEnd(v) === 'signin').length;
  const longest = Math.max(0, ...ended.map((v) => v.durationMs ?? 0));
  if (n > 0 && signin === n) return { title: n === 1 ? 'The visitor stopped at the sign-in page.' : `All ${n} stopped at the sign-in page.`, reasons: sorted };
  if (n > 0 && left === n) {
    return { title: n === 1 ? `The visitor left after ${formatDuration(longest)}.` : `All ${n} left within ${formatDuration(longest)}.`, reasons: sorted };
  }
  if (n > 0 && reached === n) return { title: n === 1 ? 'The visitor got what they came for.' : `All ${n} got what they came for.`, reasons: sorted };
  return { title: `${reached} of ${n} got what they came for${left ? `, ${left} left` : ''}${signin ? `, ${signin} stopped at sign-in` : ''}.`, reasons: sorted };
}

function MoodMeter({ mood }: { mood: number }) {
  return (
    <span className="r3-mood" aria-label={`Mood at the end ${mood} out of 10`}>
      <span className="r3-mood-bar" aria-hidden="true">
        {Array.from({ length: 10 }, (_, i) => (
          <i key={i} className={i < mood ? 'on' : undefined} />
        ))}
      </span>
      <span className="r3-mood-text">mood {mood}/10</span>
    </span>
  );
}

/**
 * One lane per visitor: who, where they came from, the route from the page they landed on to where they stopped,
 * why, and their mood at the end. Replaces the old "Why they left" list and the visits table.
 */
export function ExitLanes({
  visits,
  behaviour,
  live,
  id = 'exits',
  showCosts,
}: {
  visits: LaneVisit[];
  behaviour: TestBehaviour;
  live: boolean;
  id?: string;
  /** Per visit, shown only to us. */
  showCosts?: (key: string) => string | null;
}) {
  const head = lanesHeadline(visits, behaviour, live);
  return (
    <section id={id} className="r3-section" aria-labelledby={`${id}-title`}>
      <div className="r3-wrap">
        <div className="r3-section-head">
          <div>
            <h2 id={`${id}-title`}>{head.title}</h2>
            {head.reasons.length > 0 && (
              <ul className="r3-reasons" aria-label="Why they stopped">
                {head.reasons.map(([reason, count]) => (
                  <li key={reason}>
                    {exitReasonLabel[reason]} <span className="r3-count">{count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <p className="r3-legend" aria-hidden="true">
            <span>
              <i className="r3-legend-start" /> Landed
            </span>
            <span>
              <i className="r3-legend-left" /> Left the site
            </span>
          </p>
        </div>
        <ol className="r3-lanes" data-testid="visit-lanes">
          {visits.map((v) => {
            const end = laneEnd(v);
            const path = endPathOf(v);
            const open = v.status !== 'queued';
            const reason =
              end === 'live' && v.waiting
                ? 'Waiting at the sign-in page'
                : end === 'live' || end === 'queued' || end === 'signin'
                ? statusLabel(v.status, behaviour)
                : v.exit && end !== 'reached' && behaviour !== 'check'
                  ? exitReasonLabel[v.exit.category]
                  : statusLabel(v.status, behaviour);
            const cost = showCosts?.(v.key);
            return (
              <li key={v.key} className={`r3-lane r3-lane-${end}`} data-visit-status={v.status}>
                <div className="r3-lane-who">
                  <Orb index={v.index} size={44} />
                  <span className="r3-lane-name">
                    {open ? <Link to={v.href}>{v.personaName}</Link> : <span>{v.personaName}</span>}
                    <span className="r3-lane-source">
                      <Icon name={deviceIcon[v.device]} size={13} />
                      {v.source ? `from ${v.source}` : v.device}
                    </span>
                  </span>
                </div>
                <div className="r3-route" aria-label={`Landed on ${v.entryPath ?? 'the site'}, ${v.stepCount} steps, ${end === 'left' ? `left${path ? ` on ${path}` : ''}` : reason.toLowerCase()}`}>
                  <code className="r3-stop r3-stop-start">{v.entryPath ?? '/'}</code>
                  <span className="r3-route-line">
                    <span className="r3-route-label">
                      {v.stepCount} {v.stepCount === 1 ? 'step' : 'steps'}
                      {v.durationMs !== null ? `, ${formatDuration(v.durationMs)}` : ''}
                    </span>
                  </span>
                  <span className={`r3-stop r3-stop-${end}`}>
                    <i aria-hidden="true" />
                    {end === 'left' || end === 'other' ? (
                      <code>{path ?? 'left'}</code>
                    ) : end === 'signin' ? (
                      <>
                        <Icon name="lock" size={12} /> Sign-in
                      </>
                    ) : end === 'reached' ? (
                      'Got there'
                    ) : end === 'live' ? (
                      v.waiting ? 'At sign-in' : 'Browsing'
                    ) : (
                      'Waiting'
                    )}
                  </span>
                </div>
                <div className="r3-lane-why">
                  <strong>{reason}</strong>
                  {(v.outcome ?? v.exit?.missing) && <span>{v.outcome ?? v.exit?.missing}</span>}
                </div>
                <div className="r3-lane-end">
                  {v.finalMood !== null && <MoodMeter mood={v.finalMood} />}
                  {cost && <span className="r3-cost mono">{cost}</span>}
                  {open && (
                    <Link to={v.endHref} className="r3-play" aria-label={`Watch the visit of ${v.personaName}`}>
                      <Icon name="play" size={14} />
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

/** A problem as the report shows it: the group, how many visitors hit it, and the first moment to watch. */
export interface ProblemView {
  key: string;
  title: string;
  severity: IssueSeverity;
  description: string;
  /** The change to make, in one sentence (reports written after the editing pass). */
  fix?: string;
  /** Visitors who hit it, out of `total`. */
  hit: number;
  total: number;
  pages: string[];
  /** The first moment, on the player's clock. */
  at: string | null;
  href: string;
  /** Names the link for screen readers: "Watch Impulse buyer, step 4". */
  watchLabel: string;
  who: string[];
}

const SEVERITIES: IssueSeverity[] = ['high', 'medium', 'low'];

function ProblemCard({ p }: { p: ProblemView }) {
  return (
    <li className={`r3-problem r3-problem-${p.severity}`}>
      <div className="r3-problem-sev">
        <span className={`sev sev-${p.severity}`}>{severityLabel[p.severity]}</span>
        {p.total <= 12 && (
          <span className="r3-hits" aria-hidden="true">
            {Array.from({ length: p.total }, (_, i) => (
              <i key={i} className={i < p.hit ? 'on' : undefined} />
            ))}
          </span>
        )}
        <span className="r3-hit-text">
          <strong>
            {p.hit} of {p.total}
          </strong>{' '}
          {p.total === 1 ? 'visitor' : 'visitors'}
        </span>
      </div>
      <div className="r3-problem-text">
        <h3>
          <Link to={p.href}>{p.title}</Link>
        </h3>
        {p.description && <FoldedText text={p.description} className="r3-problem-desc" />}
        {p.fix && (
          <p className="r3-fix">
            <span className="r3-fix-label">
              <Icon name="sparkle" size={13} /> Fix
            </span>
            <span>{p.fix}</span>
          </p>
        )}
        {p.pages.length > 0 && (
          <p className="r3-pages">
            <span>Seen on</span>
            {p.pages.slice(0, 4).map((path) => (
              <code key={path}>{path}</code>
            ))}
          </p>
        )}
      </div>
      <div className="r3-problem-moment">
        {p.at && <span className="r3-clock r3-clock-light">at {p.at}</span>}
        <Link to={p.href} className="r3-watch" aria-label={p.watchLabel}>
          <Icon name="play" size={12} /> Watch this moment
        </Link>
        {p.who.length > 0 && <span className="r3-who">{p.who.slice(0, 3).join(', ') + (p.who.length > 3 ? ` and ${p.who.length - 3} more` : '')}</span>}
      </div>
    </li>
  );
}

/** The problems, worst first, with a filter by severity once there are enough to need one. */
export function ProblemCards({ problems, shown = 7 }: { problems: ProblemView[]; shown?: number }) {
  const [only, setOnly] = useState<IssueSeverity | 'all'>('all');
  const counts = Object.fromEntries(SEVERITIES.map((s) => [s, problems.filter((p) => p.severity === s).length])) as Record<IssueSeverity, number>;
  const list = only === 'all' ? problems : problems.filter((p) => p.severity === only);
  const filters = problems.length > 3 && SEVERITIES.filter((s) => counts[s] > 0).length > 1;
  return (
    <>
      {filters && (
        <div className="r3-filter" role="group" aria-label="Show problems by severity">
          <button type="button" aria-pressed={only === 'all'} onClick={() => setOnly('all')}>
            All <span>{problems.length}</span>
          </button>
          {SEVERITIES.filter((s) => counts[s] > 0).map((s) => (
            <button key={s} type="button" aria-pressed={only === s} onClick={() => setOnly(s)}>
              {severityLabel[s]} <span>{counts[s]}</span>
            </button>
          ))}
        </div>
      )}
      <ol className="r3-problems">
        {list.slice(0, only === 'all' ? shown : undefined).map((p) => (
          <ProblemCard key={p.key} p={p} />
        ))}
      </ol>
      {only === 'all' && list.length > shown && (
        <details className="r3-more">
          <summary>
            Show the other {list.length - shown} {list.length - shown === 1 ? 'problem' : 'problems'}
          </summary>
          <ol className="r3-problems">
            {list.slice(shown).map((p) => (
              <ProblemCard key={p.key} p={p} />
            ))}
          </ol>
        </details>
      )}
    </>
  );
}

/** What worked, each with the visitors that show it. */
export function Strengths({ items }: { items: { title: string; description: string; links: { key: string; label: string; href: string }[] }[] }) {
  return (
    <ul className="r3-strengths">
      {items.map((st) => (
        <li key={st.title}>
          <span className="r3-worked">
            <Icon name="check" size={14} /> Worked
          </span>
          <div>
            <h3>{st.title}</h3>
            {st.description && <p>{st.description}</p>}
          </div>
          <span className="r3-strength-links">
            {st.links.map((l) => (
              <Link key={l.key} to={l.href} className="r3-initials r3-initials-sm" title={l.label} aria-label={`Watch ${l.label}`}>
                {initialsOf(l.label)}
              </Link>
            ))}
          </span>
        </li>
      ))}
    </ul>
  );
}
